import { ChakraBox as Box } from "@airhelp/react-ui-components";
import { stickyBottomBarFirstTriggerStyles } from "components/StickyBottomBar";
import StickyBottomBarTrigger from "components/StickyBottomBar/StickyBottomBarTrigger";
import { DataContextProvider } from "contexts/DataContext";
import { useLanguage } from "contexts/LocalizationContext";
import { MobileSideMenu } from "components/MobileSideMenu";
import { ModalContextProvider } from "contexts/ModalContext";
import { StickyBottomBarContextProvider } from "contexts/StickyBottomBarContext";
import CustomHead from "modules/common/components/CustomHead";
import Footer from "modules/common/components/Footer";
import { HeaderNav, MenuItems } from "modules/common/components/HeaderNav";
import { PressLogos } from "modules/common/components/PressLogos";
import StickyBottomBarAdapter from "modules/storyblok/adapters/StickyBottomBar";
import BreadcrumbsSchema from "modules/storyblok/components/BreadcrumbsSchema";
import { renderDynamicComponent } from "modules/storyblok/DynamicComponent/DynamicComponent";
import { useTopMenu } from "hooks/useMenu";
import { ZowieControl } from "components";
import ActionButton from "./ActionButton";
import type { FlexibleTemplateProps } from "./FlexibleTemplate.d";

const FlexibleTemplate = ({
  story,
  cmsLanguagesData,
  whiteListDomains,
  serverSideStoryblokPages,
  storyblokComponentData: { trustpilotInfo, reviews },
  settings,
  googleOptimizeExperimentIds,
  nofollowUrls,
}: FlexibleTemplateProps) => {
  const {
    tag_list: tagList,
    content: {
      metaTitle,
      metaDescription,
      body,
      breadcrumbsParentPage,
      breadcrumbsPositionName,
      ogTitle,
      ogImageUrl,
      ogDescription,
      stickyBottomBarHeading,
      hideStickyBottomBar,
      stickyBottomBarVariant,
    },
  } = story;
  const language = useLanguage();
  const topMenu = useTopMenu(language);
  const noindex = tagList.includes("noindex");

  return (
    <DataContextProvider
      data={{
        reviews,
        cmsLanguagesData,
        serverSideStoryblokPages,
        trustpilotInfo,
        whiteListDomains,
        googleOptimizeExperimentIds,
        nofollowUrls,
      }}
    >
      <CustomHead
        title={metaTitle}
        description={metaDescription}
        noindex={noindex}
        ogTitle={ogTitle}
        ogDescription={ogDescription}
        ogImageUrl={ogImageUrl}
      />
      <BreadcrumbsSchema
        breadcrumbsPositionName={breadcrumbsPositionName}
        breadcrumbsParentPage={breadcrumbsParentPage}
      />
      <MobileSideMenu>
        <ModalContextProvider>
          <HeaderNav
            menuItems={<MenuItems menu={topMenu} />}
            headerButtons={<ActionButton activeButtonOption={settings?.activeButtonOption} />}
          />
          <ZowieControl />
          <Box pt={24} as="main" sx={{ "#main-page-head": { overflow: "initial" } }}>
            <StickyBottomBarTrigger type="first" styles={stickyBottomBarFirstTriggerStyles} />
            {renderDynamicComponent(body)}
          </Box>
          <PressLogos />
          <Footer />
        </ModalContextProvider>
      </MobileSideMenu>
      <StickyBottomBarAdapter
        showStickyBottomBar={!hideStickyBottomBar}
        stickyBottomBarVariant={stickyBottomBarVariant}
        stickyBottomBarHeading={stickyBottomBarHeading}
      />
    </DataContextProvider>
  );
};

export const FlexibleTemplateAdapter = (props: FlexibleTemplateProps) => (
  <StickyBottomBarContextProvider threshold={0.4}>
    <FlexibleTemplate {...props} />
  </StickyBottomBarContextProvider>
);
